import { useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui-kit/dialog';
import { Card, CardContent } from '@/components/ui-kit/card';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { Button } from '@/components/ui-kit/button';
import { Checkbox } from '@/components/ui-kit/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui-kit/radio-group';

import { Trash2, Plus, Info, Eye, EyeOff } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui-kit/tooltip';
import {
  useCompleteOAuth,
  useInitiateOAuth,
  useSetupMCPServer,
  useTestMcpAuth,
} from '@/modules/gpt-chats/hooks/use-tools';
import { showSuccessToast, showErrorToast } from '@/hooks/use-toast';
import { configParamsSchema, configParamsDefaultValues } from './utils';
import type {
  IConfigParamsFormValues,
  ConfigParamsAuthType,
  ConfigParamsTransportMethod,
} from '@/modules/gpt-chats/types/tools.service.type';

import { IMCPServerSetupPayload, IMCPConfig } from '@/modules/gpt-chats/types/tools.service.type';
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ToolAuthCard } from '@/modules/gpt-chats/components/tools/tool-card/tool-auth-card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { useTranslation } from 'react-i18next';

interface ConfigParamsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  name: string;
  toolId: string;
  mcpConfig?: IMCPConfig;
  showCard?: boolean;
  isBlocksTool?: boolean;
}

export const ConfigParamsModal = ({
  open,
  onOpenChange,
  toolId,
  mcpConfig,
  isBlocksTool,
}: ConfigParamsModalProps) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { mutateAsync: setupMCPServer, isPending } = useSetupMCPServer();
  const { mutateAsync: testMcpAuth, isPending: isTestingAuth } = useTestMcpAuth();
  const { mutateAsync: initiateOAuth } = useInitiateOAuth();
  const { mutateAsync: completeOAuth } = useCompleteOAuth();

  const form = useForm<IConfigParamsFormValues>({
    defaultValues: configParamsDefaultValues,
    resolver: zodResolver(configParamsSchema),
    mode: 'onChange',
  });

  useEffect(() => {
    if (open && mcpConfig) {
      const headersArray = mcpConfig.Headers
        ? Object.entries(mcpConfig.Headers).map(([key, value]) => ({ key, value: String(value) }))
        : [{ key: '', value: '' }];
      const queryParamsArray = mcpConfig.QueryParams
        ? Object.entries(mcpConfig.QueryParams).map(([key, value]) => ({
            key,
            value: String(value),
          }))
        : [{ key: '', value: '' }];

      const transportMethod: ConfigParamsTransportMethod =
        mcpConfig.Transport === 'sse' || mcpConfig.Transport === 'streamable_http'
          ? mcpConfig.Transport
          : 'streamable_http';

      let authType: ConfigParamsAuthType = 'none';
      let authFormValues: Partial<IConfigParamsFormValues> = {};

      if (mcpConfig.AuthConfig && mcpConfig.AuthConfig.Enabled) {
        const authConfig = mcpConfig.AuthConfig;
        if (authConfig.OAuth2Config?.GrantType === 'client_credentials') {
          authType = 'oauth2_client_credentials' as ConfigParamsAuthType;
          authFormValues = {
            clientId: authConfig.OAuth2Config.ClientID || '',
            clientSecret: authConfig.OAuth2Config.ClientSecret || '',
            tokenUrl: authConfig.OAuth2Config.TokenURL || '',
            scopes: authConfig.OAuth2Config.Scopes?.join(', ') || '',
          };
        } else if (authConfig.OAuth2Config?.GrantType === 'authorization_code') {
          authType = 'oauth2_authorization_code' as ConfigParamsAuthType;
          authFormValues = {
            clientIdAuthCode: authConfig.OAuth2Config.ClientID || '',
            clientSecretAuthCode: authConfig.OAuth2Config.ClientSecret || '',
            tokenUrlAuthCode: authConfig.OAuth2Config.TokenURL || '',
            authorizationUrl: authConfig.OAuth2Config.AuthorizationEndpoint || '',
            redirectUri: authConfig.OAuth2Config.RedirectURI || '',
            scopesAuthCode: authConfig.OAuth2Config.Scopes?.join(', ') || '',
            isAuthenticated: !!authConfig.OAuth2Config.AccessToken,
          };
        } else if (authConfig.APIToken) {
          authType = 'bearer';
          authFormValues = {
            apiToken: authConfig.APIToken || '',
          };
        }
      }

      const formValues: Partial<IConfigParamsFormValues> = {
        serverUrl: mcpConfig.ServerURL || '',
        transportMethod,
        trustMcpServer: mcpConfig.TrustMCPServer || false,
        headers: headersArray.length > 0 ? headersArray : [{ key: '', value: '' }],
        queryParams: queryParamsArray.length > 0 ? queryParamsArray : [{ key: '', value: '' }],
        ...authFormValues,
      };

      form.reset({ ...configParamsDefaultValues, ...formValues, authType });
    } else if (open && !mcpConfig) {
      form.reset(configParamsDefaultValues);
    }
  }, [open, mcpConfig, form]);

  const trustMcpServer = form.watch('trustMcpServer');
  const authType = form.watch('authType');
  const isAuthenticated = form.watch('isAuthenticated');

  const [showClientSecret, setShowClientSecret] = useState(false);
  const [showClientSecretAuthCode, setShowClientSecretAuthCode] = useState(false);

  const addHeader = () => {
    const currentHeaders = form.getValues('headers');
    form.setValue('headers', [...currentHeaders, { key: '', value: '' }]);
  };

  const removeHeader = (index: number) => {
    const currentHeaders = form.getValues('headers');
    form.setValue(
      'headers',
      currentHeaders.filter((_, i) => i !== index)
    );
  };

  const updateHeader = (index: number, field: 'key' | 'value', value: string) => {
    const currentHeaders = form.getValues('headers');
    const newHeaders = [...currentHeaders];
    newHeaders[index][field] = value;
    form.setValue('headers', newHeaders);
  };

  const addQueryParam = () => {
    const currentParams = form.getValues('queryParams');
    form.setValue('queryParams', [...currentParams, { key: '', value: '' }]);
  };

  const removeQueryParam = (index: number) => {
    const currentParams = form.getValues('queryParams');
    form.setValue(
      'queryParams',
      currentParams.filter((_, i) => i !== index)
    );
  };

  const updateQueryParam = (index: number, field: 'key' | 'value', value: string) => {
    const currentParams = form.getValues('queryParams');
    const newQueryParams = [...currentParams];
    newQueryParams[index][field] = value;
    form.setValue('queryParams', newQueryParams);
  };

  const handleTestAuthentication = async () => {
    try {
      const authType = form.watch('authType');
      let oauth2Config: Record<string, unknown> = {};

      if (authType === 'bearer') {
        oauth2Config = {
          APIToken: form.watch('apiToken'),
          APIKeyPrefix: 'Bearer',
          APIKeyName: 'Authorization',
        };
      } else if (authType === 'oauth2_client_credentials') {
        oauth2Config = {
          ClientID: form.watch('clientId'),
          ClientSecret: form.watch('clientSecret'),
          TokenURL: form.watch('tokenUrl'),
          Scopes: (form.watch('scopes') || '')
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
          GrantType: 'client_credentials',
        };
      } else if (authType === 'oauth2_authorization_code') {
        oauth2Config = {
          ClientID: form.watch('clientIdAuthCode'),
          ClientSecret: form.watch('clientSecretAuthCode'),
          TokenURL: form.watch('tokenUrlAuthCode'),
          AuthorizationEndpoint: form.watch('authorizationUrl'),
          RedirectURI: form.watch('redirectUri'),
          Scopes: (form.watch('scopesAuthCode') || '')
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
          GrantType: 'authorization_code',
        };
      }

      const result = await testMcpAuth({
        toolId,
        payload: {
          auth_config: oauth2Config,
        },
        isBlocksTool,
      });

      if (result.is_success) {
        showSuccessToast({
          title: t('SUCCESS'),
          description: t('AUTH_TEST_SUCCESSFUL'),
        });
      } else {
        showErrorToast({
          title: t('FAILED'),
          errors: result.message || t('AUTH_TEST_FAILED'),
        });
      }
    } catch (error) {
      showErrorToast({
        title: t('FAILED'),
        errors: error,
      });
    }
  };

  const handleAuthorize = async () => {
    try {
      const values = form.getValues();

      if (!values.serverUrl) {
        showErrorToast({
          title: t('VALIDATION_ERROR'),
          errors: t('SERVER_URL_REQUIRED'),
        });
        return;
      }

      if (!values.clientIdAuthCode || !values.clientSecretAuthCode || !values.redirectUri) {
        showErrorToast({
          title: t('VALIDATION_ERROR'),
          errors: t('OAUTH_FIELDS_REQUIRED'),
        });
        return;
      }

      const oauthConfig = {
        ClientID: values.clientIdAuthCode,
        ClientSecret: values.clientSecretAuthCode,
        TokenURL: values.tokenUrlAuthCode || '',
        AuthorizationEndpoint: values.authorizationUrl || '',
        RedirectURI: values.redirectUri,
        Scopes: (values.scopesAuthCode || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        GrantType: 'authorization_code',
      };

      const result = await initiateOAuth({
        toolId,
        payload: {
          oauth_config: oauthConfig,
        },
      });

      if (result.is_success && result.authorization_url) {
        const authWindow = window.open(
          result.authorization_url,
          'oauth_authorization',
          'width=600,height=700,scrollbars=yes'
        );

        if (!authWindow) {
          showErrorToast({
            title: t('POPUP_BLOCKED'),
            errors: t('ALLOW_POPUPS'),
          });
          return;
        }

        showSuccessToast({
          title: t('AUTHORIZATION_STARTED'),
          description: t('COMPLETE_AUTH_IN_WINDOW'),
        });

        window.addEventListener('message', handleOAuthCallback);
      } else {
        showErrorToast({
          title: t('AUTHORIZATION_FAILED'),
          errors: result.message || t('FAILED_TO_INITIATE_OAUTH'),
        });
      }
    } catch (error) {
      console.error('Failed to initiate OAuth:', error);
      showErrorToast({
        title: t('AUTHORIZATION_FAILED'),
        errors: error,
      });
    }
  };

  const handleOAuthCallback = async (event: MessageEvent) => {
    if (event.origin !== window.location.origin) {
      return;
    }

    if (event.data.type === 'oauth_callback') {
      const { code, state } = event.data;

      if (!code) {
        showErrorToast({
          title: t('AUTHORIZATION_FAILED'),
          errors: t('NO_AUTH_CODE_RECEIVED'),
        });
        window.removeEventListener('message', handleOAuthCallback);
        return;
      }

      try {
        const result = await completeOAuth({
          toolId,
          payload: {
            code,
            state: state || '',
            is_open_tool: isBlocksTool,
          },
        });

        if (result.is_success) {
          form.setValue('isAuthenticated', true);

          showSuccessToast({
            title: t('AUTHORIZATION_SUCCESSFUL'),
            description: t('OAUTH_COMPLETED_SUCCESSFULLY'),
          });

          queryClient.invalidateQueries({
            queryKey: ['tools', 'details', toolId],
          });
        } else {
          form.setValue('isAuthenticated', false);
          showErrorToast({
            title: t('AUTHORIZATION_FAILED'),
            errors: result.message || t('FAILED_TO_COMPLETE_OAUTH'),
          });
        }
      } catch (error) {
        console.error('Failed to complete OAuth:', error);
        form.setValue('isAuthenticated', false);
        showErrorToast({
          title: t('AUTHORIZATION_FAILED'),
          errors: error,
        });
      } finally {
        window.removeEventListener('message', handleOAuthCallback);
      }
    }
  };

  useEffect(() => {
    return () => {
      window.removeEventListener('message', handleOAuthCallback);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = form.handleSubmit(async (values) => {
    try {
      const headersObj: Record<string, string> = {};
      values.headers.forEach((h) => {
        if (h.key && h.value) {
          headersObj[h.key] = h.value;
        }
      });

      const queryParamsObj: Record<string, string> = {};
      values.queryParams.forEach((q) => {
        if (q.key && q.value) {
          queryParamsObj[q.key] = q.value;
        }
      });

      const authConfig: IMCPServerSetupPayload['auth_config'] = {
        Enabled: values.authType !== 'none',
        AutoDiscoverMetadata: false,
      };

      if (values.authType === 'bearer') {
        authConfig.APIToken = values.apiToken || '';
        authConfig.APIKeyPrefix = 'Bearer';
        authConfig.APIKeyName = 'Authorization';
      } else if (values.authType === 'oauth2_client_credentials') {
        authConfig.OAuth2Config = {
          ClientID: values.clientId || '',
          ClientSecret: values.clientSecret || '',
          TokenURL: values.tokenUrl || '',
          Scopes: (values.scopes || '')
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
          GrantType: 'client_credentials',
        };
      } else if (values.authType === 'oauth2_authorization_code') {
        authConfig.OAuth2Config = {
          ClientID: values.clientIdAuthCode || '',
          ClientSecret: values.clientSecretAuthCode || '',
          TokenURL: values.tokenUrlAuthCode || '',
          AuthorizationEndpoint: values.authorizationUrl || '',
          RedirectURI: values.redirectUri || '',
          Scopes: (values.scopesAuthCode || '')
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
          GrantType: 'authorization_code',
        };
      }

      const payload: IMCPServerSetupPayload = {
        server_url: values.serverUrl,
        transport: values.transportMethod,
        headers: headersObj,
        query_params: queryParamsObj,
        trust_mcp_server: values.trustMcpServer,
        auth_config: authConfig,
        skip_discovery: false,
        is_open_tool: isBlocksTool,
      };

      const result = await setupMCPServer({ toolId, payload });

      if (result.is_success) {
        showSuccessToast({
          title: t('SUCCESS'),
          description: t('MCP_SERVER_CONFIGURED_SUCCESSFULLY'),
        });
        queryClient.invalidateQueries({ queryKey: ['tools', 'details', toolId] });
        form.reset();
        onOpenChange(false);
      } else {
        showErrorToast({
          title: t('FAILED'),
          errors: result.message || t('FAILED_TO_CONFIGURE_MCP'),
        });
      }
    } catch (error) {
      console.error('Failed to setup MCP server:', error);
      showErrorToast({
        title: t('FAILED'),
        errors: error,
      });
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">{t('CONFIGURE_PARAMS')}</DialogTitle>
        </DialogHeader>

        <FormProvider {...form}>
          <div className="space-y-6 py-4">
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-4">
                <Label className="mb-2">{t('TOOL_METHOD')}</Label>
                <RadioGroup
                  value={form.watch('transportMethod')}
                  onValueChange={(value) =>
                    form.setValue('transportMethod', value as ConfigParamsTransportMethod)
                  }
                >
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="sse" id="sse" />
                      <Label htmlFor="sse" className="font-normal">
                        SSE
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="streamable_http" id="streamable" />
                      <Label htmlFor="streamable" className="font-normal">
                        Streamable HTTP
                      </Label>
                    </div>
                  </div>
                </RadioGroup>
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="serverUrl" className="mb-2">
                  URL <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="serverUrl"
                  type="url"
                  placeholder={t('ENTER_URL')}
                  {...form.register('serverUrl')}
                  className="w-full"
                />
              </div>
              {form.formState.errors.serverUrl && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.serverUrl.message}
                </p>
              )}
            </div>

            <Card>
              <CardContent>
                <div className="flex flex-col gap-6">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="authType" className="mb-2">
                      {t('AUTHENTICATION_TYPE')}
                    </Label>
                    <Select
                      value={authType}
                      onValueChange={(value) =>
                        form.setValue('authType', value as ConfigParamsAuthType)
                      }
                    >
                      <SelectTrigger className="w-full sm:w-1/2">
                        <SelectValue placeholder={t('SELECT_AUTH_TYPE')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t('AUTH_NONE')}</SelectItem>
                        <SelectItem value="bearer">{t('AUTH_BEARER')}</SelectItem>
                        <SelectItem value="basic">{t('AUTH_BASIC')}</SelectItem>
                        <SelectItem value="api_token">{t('AUTH_API_TOKEN')}</SelectItem>
                        <SelectItem value="oauth2_client_credentials">
                          OAuth 2.1 - Client Credentials
                        </SelectItem>
                        {/* <SelectItem value="oauth2_authorization_code">
                          OAuth 2.1 - Authorization Code
                        </SelectItem> */}
                      </SelectContent>
                    </Select>
                  </div>

                  {(authType === 'bearer' || authType === 'basic' || authType === 'api_token') && (
                    <ToolAuthCard
                      authType={authType}
                      showAuthTypeSelector={false}
                      excludeAuthTypes={['oauth2']}
                      showCard={false}
                    />
                  )}

                  {/* OAuth 2.1 Client Credentials Card */}
                  {authType === 'oauth2_client_credentials' && (
                    <div className="rounded-lg bg-muted/50 p-4">
                      <div className="flex flex-col gap-4">
                        <div className="flex items-center gap-2">
                          <label className="font-medium">Configuration</label>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="h-4 w-4 cursor-help text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Configure OAuth 2.1 Client Credentials authentication</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="flex flex-col gap-2">
                            <Label htmlFor="clientId" className="mb-2">
                              Client ID <span className="text-destructive">*</span>
                            </Label>
                            <Input
                              id="clientId"
                              placeholder="Enter client ID"
                              {...form.register('clientId')}
                            />
                          </div>
                          <div className="flex flex-col gap-2">
                            <Label htmlFor="clientSecret" className="mb-2">
                              Client Secret <span className="text-destructive">*</span>
                            </Label>
                            <div className="relative">
                              <Input
                                id="clientSecret"
                                type={showClientSecret ? 'text' : 'password'}
                                placeholder="Enter client secret"
                                {...form.register('clientSecret')}
                                className="pr-10"
                              />
                              <button
                                type="button"
                                onClick={() => setShowClientSecret(!showClientSecret)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                              >
                                {showClientSecret ? (
                                  <EyeOff className="h-4 w-4" />
                                ) : (
                                  <Eye className="h-4 w-4" />
                                )}
                              </button>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="flex flex-col gap-2">
                            <Label htmlFor="tokenUrl" className="mb-2">
                              Token URL <span className="text-destructive">*</span>
                            </Label>
                            <Input
                              id="tokenUrl"
                              type="url"
                              placeholder="https://example.com/oauth/token"
                              {...form.register('tokenUrl')}
                            />
                          </div>
                          <div className="flex flex-col gap-2">
                            <Label htmlFor="scopes" className="mb-2">
                              Scopes (comma-separated)
                            </Label>
                            <Input
                              id="scopes"
                              placeholder="read, write"
                              {...form.register('scopes')}
                            />
                          </div>
                        </div>

                        <Button
                          type="button"
                          onClick={handleTestAuthentication}
                          className="mt-3 w-fit"
                          disabled={isTestingAuth}
                        >
                          {isTestingAuth ? t('TESTING') : t('TEST_AUTHENTICATION')}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* OAuth 2.1 Authorization Code Card */}
                  {authType === 'oauth2_authorization_code' && (
                    <div className="rounded-lg bg-muted/50 p-4">
                      <div className="flex flex-col gap-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <label className="font-medium">Configuration</label>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="h-4 w-4 cursor-help text-muted-foreground" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Configure OAuth 2.1 Authorization Code authentication</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          {isAuthenticated && (
                            <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-800">
                              {t('AUTHENTICATED')}
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="flex flex-col gap-2">
                            <Label htmlFor="clientIdAuthCode" className="mb-2">
                              Client ID <span className="text-destructive">*</span>
                            </Label>
                            <Input
                              id="clientIdAuthCode"
                              placeholder="Enter client ID"
                              {...form.register('clientIdAuthCode')}
                            />
                          </div>
                          <div className="flex flex-col gap-2">
                            <Label htmlFor="clientSecretAuthCode" className="mb-2">
                              Client Secret <span className="text-destructive">*</span>
                            </Label>
                            <div className="relative">
                              <Input
                                id="clientSecretAuthCode"
                                type={showClientSecretAuthCode ? 'text' : 'password'}
                                placeholder="Enter client secret"
                                {...form.register('clientSecretAuthCode')}
                                className="pr-10"
                              />
                              <button
                                type="button"
                                onClick={() =>
                                  setShowClientSecretAuthCode(!showClientSecretAuthCode)
                                }
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                              >
                                {showClientSecretAuthCode ? (
                                  <EyeOff className="h-4 w-4" />
                                ) : (
                                  <Eye className="h-4 w-4" />
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="flex flex-col gap-2">
                            <Label htmlFor="authorizationUrl" className="mb-2">
                              Authorization URL <span className="text-destructive">*</span>
                            </Label>
                            <Input
                              id="authorizationUrl"
                              type="url"
                              placeholder="https://example.com/oauth/authorize"
                              {...form.register('authorizationUrl')}
                            />
                          </div>
                          <div className="flex flex-col gap-2">
                            <Label htmlFor="tokenUrlAuthCode" className="mb-2">
                              Token URL <span className="text-destructive">*</span>
                            </Label>
                            <Input
                              id="tokenUrlAuthCode"
                              type="url"
                              placeholder="https://example.com/oauth/token"
                              {...form.register('tokenUrlAuthCode')}
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="flex flex-col gap-2">
                            <Label htmlFor="redirectUri" className="mb-2">
                              Redirect URI <span className="text-destructive">*</span>
                            </Label>
                            <Input
                              id="redirectUri"
                              type="url"
                              placeholder="https://your-app.com/callback"
                              {...form.register('redirectUri')}
                            />
                          </div>
                          <div className="flex flex-col gap-2">
                            <Label htmlFor="scopesAuthCode" className="mb-2">
                              Scopes (comma-separated)
                            </Label>
                            <Input
                              id="scopesAuthCode"
                              placeholder="read, write"
                              {...form.register('scopesAuthCode')}
                            />
                          </div>
                        </div>
                        <Button
                          type="button"
                          onClick={handleAuthorize}
                          disabled={isAuthenticated}
                          className="mt-3 w-fit"
                        >
                          {isAuthenticated ? t('AUTHORIZED') : t('AUTHORIZE')}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <div className="flex flex-col gap-6">
                  <div className="mb-2 flex items-center gap-3 rounded-md bg-yellow-100 px-3 py-2 text-sm text-yellow-800">
                    <Info className="h-6 w-6 min-w-[1.5rem] text-yellow-600" />
                    <span>
                      <strong>Caution:</strong> Sending sensitive data (like tokens, credentials, or
                      secrets) via HTTP headers can expose them to security risks. Only include
                      values you trust and avoid sharing confidential information unless absolutely
                      necessary.
                    </span>
                  </div>
                  <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-2">
                      <Label className="font-bold">Headers</Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="h-4 w-4 cursor-help text-muted-foreground" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Set custom headers for your MCP server requests</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>

                    <div className="flex flex-col gap-4">
                      <div className="grid grid-cols-[1fr_1fr_40px] gap-4 font-medium">
                        <Label>Key</Label>
                        <Label>Value</Label>
                        <div></div>
                      </div>

                      {form.watch('headers').map((header, index) => (
                        <div
                          key={index}
                          className="grid grid-cols-[1fr_1fr_40px] items-center gap-4"
                        >
                          <Input
                            placeholder="Header Key"
                            value={header.key}
                            onChange={(e) => updateHeader(index, 'key', e.target.value)}
                          />
                          <Input
                            placeholder="Enter value"
                            value={header.value}
                            onChange={(e) => updateHeader(index, 'value', e.target.value)}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeHeader(index)}
                            className="h-8 w-8 flex-shrink-0 text-destructive hover:text-destructive"
                            disabled={form.watch('headers').length === 1}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ))}

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={addHeader}
                        className="w-fit gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        Add
                      </Button>
                    </div>
                  </div>

                  <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-2">
                      <Label className="font-bold">Query Params</Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="h-4 w-4 cursor-help text-muted-foreground" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Set query parameters for your MCP server requests</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>

                    <div className="flex flex-col gap-4">
                      <div className="grid grid-cols-[1fr_1fr_40px] gap-4 font-medium">
                        <Label>Key</Label>
                        <Label>Value</Label>
                        <div></div>
                      </div>

                      {form.watch('queryParams').map((param, index) => (
                        <div
                          key={index}
                          className="grid grid-cols-[1fr_1fr_40px] items-center gap-4"
                        >
                          <Input
                            placeholder="Query Key"
                            value={param.key}
                            onChange={(e) => updateQueryParam(index, 'key', e.target.value)}
                          />
                          <Input
                            placeholder="Enter value"
                            value={param.value}
                            onChange={(e) => updateQueryParam(index, 'value', e.target.value)}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeQueryParam(index)}
                            className="h-8 w-8 flex-shrink-0 text-destructive hover:text-destructive"
                            disabled={form.watch('queryParams').length === 1}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ))}

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={addQueryParam}
                        className="w-fit gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        Add
                      </Button>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="trustMcp"
                      checked={trustMcpServer}
                      onCheckedChange={(checked) =>
                        form.setValue('trustMcpServer', checked as boolean)
                      }
                    />
                    <Label htmlFor="trustMcp" className="cursor-pointer font-medium">
                      {t('TRUST_MCP_SERVICE')}
                    </Label>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
              {t('CANCEL')}
            </Button>
            <Button
              onClick={handleSave}
              disabled={isPending || !form.formState.isValid || !trustMcpServer}
            >
              {isPending ? t('SAVING') : t('SAVE')}
            </Button>
          </div>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};
